import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const API_TIMEOUT = 10000; // 10 seconds

// Toast handler for error notifications
let toastErrorHandler: ((message: string, type?: 'error' | 'warning' | 'info') => void) | null = null;

export const setToastErrorHandler = (handler: (message: string, type?: 'error' | 'warning' | 'info') => void) => {
  toastErrorHandler = handler;
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          localStorage.setItem('accessToken', accessToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError: any) {
        // Only redirect to login if refresh token is actually invalid (401)
        // For network errors, don't auto-logout
        if (refreshError.response?.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        } else {
          // For network errors, just reject the promise without logout
          console.warn('Token refresh failed due to network error:', refreshError.message);
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle 429 Too Many Requests - exponential backoff retry
    // Skip retries for auth endpoints as they have their own caching and circuit breaker
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');

    if (error.response?.status === 429 && !isAuthEndpoint && !originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    if (error.response?.status === 429 && !isAuthEndpoint && originalRequest._retryCount < 3) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      // Exponential backoff: 1s, 2s, 4s
      const backoffDelay = Math.min(1000 * Math.pow(2, originalRequest._retryCount - 1), 4000);

      console.log(`Rate limited. Retrying in ${backoffDelay}ms (attempt ${originalRequest._retryCount}/3)`);

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(apiClient(originalRequest));
        }, backoffDelay);
      });
    }

    // Handle other HTTP errors
    if (error.response) {
      const { status, data } = error.response;

      // Log error for debugging
      console.error(`API Error ${status}:`, data);

      // Don't show toast for certain requests to avoid spam
      const shouldShowToast = !(
        originalRequest.url?.includes('/health') || // Don't show toast for health checks
        originalRequest.url?.includes('/auth/') || // Don't show toast for auth endpoints (use inline form errors)
        (status === 429 && originalRequest._isRetry) // Don't show toast for retry attempts
      );

      // Show toast notifications for errors (with conditions)
      const errorMessage = data?.message || data?.error || 'An unexpected error occurred';

      if (shouldShowToast) {
        switch (status) {
          case 403:
            // Forbidden - insufficient permissions
            toastErrorHandler?.('Akses ditolak: Izin tidak mencukupi', 'error');
            break;
          case 404:
            // Not found
            toastErrorHandler?.('Resource tidak ditemukan', 'error');
            break;
          case 429:
            // Too many requests - rate limited
            // Only show toast if this is the final retry attempt
            if (originalRequest._retryCount >= 3) {
              toastErrorHandler?.('Terlalu banyak permintaan. Silakan coba lagi nanti.', 'warning');
            }
            break;
          case 500:
            // Internal server error
            toastErrorHandler?.('Terjadi kesalahan server. Silakan coba lagi nanti.', 'error');
            break;
          case 400:
            // Bad request - validation error
            toastErrorHandler?.(errorMessage, 'error');
            break;
          default:
            if (status >= 500) {
              toastErrorHandler?.('Terjadi kesalahan server', 'error');
            } else if (status >= 400) {
              toastErrorHandler?.(errorMessage, 'error');
            }
        }
      }
    } else if (error.request) {
      // Network error - backend is not reachable
      console.error('Network error. Backend service may be down.');

      // Don't show toast for auth endpoints
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/');

      if (!isAuthEndpoint) {
        const errorMessage = error.code === 'ECONNREFUSED' || error.message.includes('ERR_NETWORK')
          ? 'Backend service tidak dapat dihubungi. Sistem sedang dalam perbaikan.'
          : 'Kesalahan jaringan. Periksa koneksi internet Anda.';

        toastErrorHandler?.(errorMessage, 'error');
      }

      // Trigger a health check when network errors occur
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('backend:error', {
          detail: { type: 'network', error }
        }));
      }
    } else {
      // Other error
      console.error('An unexpected error occurred:', error.message);

      // Don't show toast for auth endpoints
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
      if (!isAuthEndpoint) {
        toastErrorHandler?.('Terjadi kesalahan yang tidak terduga', 'error');
      }
    }

    return Promise.reject(error);
  }
);

// Generic API methods
class ApiClient {
  // Generic GET request
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await apiClient.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  // Generic POST request
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await apiClient.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  // Generic PUT request
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await apiClient.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  // Generic PATCH request
  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  // Generic DELETE request
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await apiClient.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // File upload
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      };
    }

    const response = await apiClient.post<ApiResponse<T>>(url, formData, config);
    return response.data;
  }

  // Download file
  async download(url: string, filename?: string): Promise<void> {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    return this.get('/health');
  }
}

// Export singleton instance
export const api = new ApiClient();

// Export axios instance for custom requests
export { apiClient };

// Export types
export type { AxiosRequestConfig };